// src/pages/PublicProfile.js
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Pagination,
  Stack,
  Typography,
} from "@mui/material";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";

import { getRoleDisplayName } from "../lib/roleLabels";
import api from "../lib/api";
import FieldsPanel from "../components/FieldsPanel";
import { getCategoryStyle } from "../utils/blogTaxonomy";

const POSTS_PER_PAGE = 6;

function formatDate(value) {
  if (!value) return "";
  return new Date(value).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function formatJoined(value) {
  if (!value) return "";
  return new Date(value).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });
}

function categoryChipSx(category) {
  const style = getCategoryStyle(category);
  return {
    minHeight: 22,
    height: "auto",
    maxWidth: "100%",
    color: style.color,
    bgcolor: style.background,
    border: `1px solid ${style.color}22`,
    fontSize: 11,
    fontWeight: 700,
    "& .MuiChip-label": {
      py: 0.25,
      whiteSpace: "normal",
      overflow: "visible",
      textOverflow: "clip",
      overflowWrap: "anywhere",
    },
  };
}

function possessiveLabel(name) {
  const cleanName = String(name || "User").trim() || "User";
  return cleanName.endsWith("s") ? `${cleanName}'` : `${cleanName}'s`;
}

export default function PublicProfile() {
  const params = useParams();
  const navigate = useNavigate();

  const handle = useMemo(() => {
    const raw = params.username || "";
    try {
      return decodeURIComponent(raw);
    } catch {
      return raw;
    }
  }, [params.username]);

  const isNumericId = useMemo(() => /^\d+$/.test(String(handle || "")), [handle]);

  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [publishedPosts, setPublishedPosts] = useState([]);
  const [publishedPage, setPublishedPage] = useState(1);
  const [fieldCount, setFieldCount] = useState(0);

  const normalizeForBlogCard = useCallback((p) => {
    const imageFromImage = typeof p.image === "string" ? p.image : null;
    const imageFromFeatured =
      typeof p.featured_image === "string"
        ? p.featured_image
        : p.featured_image?.url || null;

    return {
      ...p,
      image: imageFromImage || imageFromFeatured || null,
      date: p.date ?? p.published_at ?? p.created_at ?? "",
      author:
        typeof p.author === "string"
          ? p.author
          : p.author?.username ?? p.author_username ?? "",
    };
  }, []);

  const profileBase = useMemo(() => {
    if (isNumericId) {
      const id = String(handle);
      return {
        candidates: [
          `/profile/id/${encodeURIComponent(id)}/`,
          `/profile/${encodeURIComponent(id)}/`,
          `/users/${encodeURIComponent(id)}/profile/`,
        ],
        postsCandidates: [
          `/profile/id/${encodeURIComponent(id)}/posts/`,
          `/profile/${encodeURIComponent(id)}/posts/`,
          `/users/${encodeURIComponent(id)}/profile/posts/`,
        ],
      };
    }

    const username = String(handle || "");
    return {
      candidates: [`/profile/u/${encodeURIComponent(username)}/`],
      postsCandidates: [`/profile/u/${encodeURIComponent(username)}/posts/`],
    };
  }, [handle, isNumericId]);

  const getFirstOk = async (paths) => {
    let lastErr = null;
    for (const path of paths) {
      try {
        const response = await api.get(path);
        return { data: response.data, path };
      } catch (error) {
        lastErr = error;
      }
    }
    throw lastErr;
  };

  const fetchProfile = async () => {
    const { data } = await getFirstOk(profileBase.candidates);
    setProfile(data);
  };

  const fetchPublishedPosts = async () => {
    const paths = profileBase.postsCandidates.map((path) => `${path}?status=published`);
    const { data } = await getFirstOk(paths);
    const items = data?.results ?? data ?? [];
    setPublishedPosts(items.map(normalizeForBlogCard));
  };

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setLoading(true);
        await fetchProfile();
        await fetchPublishedPosts();
      } catch (error) {
        console.error(error);
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handle]);

  useEffect(() => {
    setPublishedPage(1);
  }, [publishedPosts.length]);

  const displayName = useMemo(() => {
    if (!profile) return "";
    const fullName = `${profile.first_name || ""} ${profile.last_name || ""}`.trim();
    return fullName || profile.username || "";
  }, [profile]);
  const profileFirstName = String(profile?.first_name || "").trim();
  const profileLastName = String(profile?.last_name || "").trim();

  const initials = useMemo(() => {
    if (!profile) return "?";
    const fullName = `${profile.first_name || ""} ${profile.last_name || ""}`.trim();
    if (fullName) {
      return fullName
        .split(/\s+/)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase())
        .join("");
    }
    return (profile.username?.[0] || "?").toUpperCase();
  }, [profile]);

  const publishedPageCount = Math.max(1, Math.ceil(publishedPosts.length / POSTS_PER_PAGE));
  const visiblePublishedPosts = publishedPosts.slice(
    (publishedPage - 1) * POSTS_PER_PAGE,
    publishedPage * POSTS_PER_PAGE
  );
  const postsTitle = profile?.is_owner ? "My Posts" : `${possessiveLabel(displayName)} Posts`;

  if (loading) {
    return (
      <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!profile) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Profile not found.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1288, mx: "auto", p: { xs: 2, md: 3 } }}>
      <Card
        sx={{
          p: { xs: 2.5, md: 3 },
          mb: 3,
          bgcolor: "#fff",
          border: "1px solid #e4e4e4",
          borderRadius: 2,
          boxShadow: "0 2px 8px rgba(15, 23, 42, 0.08)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2.5,
            alignItems: "flex-start",
          }}
        >
          <Box sx={{ display: { xs: "flex", sm: "contents" }, alignItems: "center", gap: 2 }}>
            <Avatar
              src={profile.avatar || undefined}
              sx={{
                width: 78,
                height: 78,
                bgcolor: "#f7b900",
                color: "#fff",
                fontSize: 25,
                fontWeight: 800,
                border: "4px solid #fff",
                boxShadow: "0 4px 14px rgba(15, 23, 42, 0.18)",
              }}
            >
              {initials}
            </Avatar>
            <Box sx={{ display: { xs: "block", sm: "none" }, minWidth: 0 }}>
              <Typography sx={{ fontSize: 20, fontWeight: 800, lineHeight: 1.2, overflowWrap: "anywhere" }}>
                {profileFirstName || displayName}
              </Typography>
              {profileLastName ? (
                <Typography sx={{ mt: 0.25, fontSize: 20, fontWeight: 800, lineHeight: 1.2, overflowWrap: "anywhere" }}>
                  {profileLastName}
                </Typography>
              ) : null}
            </Box>
          </Box>

          <Box sx={{ flex: 1, minWidth: 0, width: { xs: "100%", sm: "auto" } }}>
            <Typography
              variant="h5"
              sx={{
                display: { xs: "none", sm: "block" },
                fontWeight: 800,
                lineHeight: 1.2,
                overflowWrap: "anywhere",
                wordBreak: "break-word",
              }}
            >
              {displayName}
            </Typography>

            {profile.is_owner ? (
              <Button
                variant="outlined"
                startIcon={<EditOutlinedIcon />}
                onClick={() => navigate("/profile")}
                sx={{
                  display: { xs: "inline-flex", sm: "none" },
                  mt: 1.25,
                  maxWidth: "100%",
                  color: "#111",
                  borderColor: "#dfdfdf",
                  textTransform: "none",
                  fontWeight: 700,
                  "&:hover": { borderColor: "#f7b900", bgcolor: "#fffaf0" },
                }}
              >
                Edit profile
              </Button>
            ) : null}

            {getRoleDisplayName(profile.role, profile.role_name) ? (
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.5, color: "text.secondary" }}>
                <BadgeOutlinedIcon sx={{ fontSize: 16 }} />
                <Typography sx={{ fontSize: 14 }}>
                  {getRoleDisplayName(profile.role, profile.role_name)}
                </Typography>
              </Stack>
            ) : null}

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={{ xs: 1, sm: 2.5 }}
              rowGap={1}
              sx={{
                mt: 2.5,
                flexWrap: { xs: "nowrap", sm: "wrap" },
                alignItems: "flex-start",
                color: "text.secondary",
                minWidth: 0,
              }}
            >
              {profile.is_owner && profile.email ? (
                <Stack direction="row" spacing={0.75} alignItems="flex-start" sx={{ maxWidth: "100%", minWidth: 0 }}>
                  <EmailOutlinedIcon sx={{ fontSize: 16, mt: 0.25, flexShrink: 0 }} />
                  <Typography sx={{ fontSize: 14, overflowWrap: "anywhere", wordBreak: "break-word", minWidth: 0 }}>
                    {profile.email}
                  </Typography>
                </Stack>
              ) : null}
              {profile.location ? (
                <Stack direction="row" spacing={0.75} alignItems="flex-start" sx={{ maxWidth: "100%", minWidth: 0 }}>
                  <LocationOnOutlinedIcon sx={{ fontSize: 16, mt: 0.25, flexShrink: 0 }} />
                  <Typography sx={{ fontSize: 14, overflowWrap: "anywhere", wordBreak: "break-word", minWidth: 0 }}>
                    {profile.location}
                  </Typography>
                </Stack>
              ) : null}
              {profile.headline ? (
                <Stack direction="row" spacing={0.75} alignItems="flex-start" sx={{ maxWidth: "100%", minWidth: 0 }}>
                  <BusinessOutlinedIcon sx={{ fontSize: 16, mt: 0.25, flexShrink: 0 }} />
                  <Typography sx={{ fontSize: 14, overflowWrap: "anywhere", wordBreak: "break-word", minWidth: 0 }}>
                    {profile.headline}
                  </Typography>
                </Stack>
              ) : null}
              {profile.date_joined || profile.joined ? (
                <Stack direction="row" spacing={0.75} alignItems="flex-start" sx={{ maxWidth: "100%", minWidth: 0 }}>
                  <CalendarTodayOutlinedIcon sx={{ fontSize: 16, mt: 0.25, flexShrink: 0 }} />
                  <Typography sx={{ fontSize: 14, overflowWrap: "anywhere", wordBreak: "break-word", minWidth: 0 }}>
                    Joined {formatJoined(profile.date_joined || profile.joined)}
                  </Typography>
                </Stack>
              ) : null}
            </Stack>

            {profile.bio ? (
              <Box
                sx={{
                  mt: 2.25,
                  maxWidth: 790,
                  width: "100%",
                  pt: 1.75,
                  borderTop: "1px solid #e8e8e8",
                  textAlign: "left",
                }}
              >
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5, color: "text.secondary" }}>
                  <NotesOutlinedIcon sx={{ fontSize: 17 }} />
                  <Typography sx={{ fontSize: 12, fontWeight: 800, textTransform: "uppercase" }}>
                    Bio
                  </Typography>
                </Stack>
                <Typography
                  sx={{
                    width: "100%",
                    whiteSpace: "pre-wrap",
                    lineHeight: 1.65,
                    overflowWrap: "anywhere",
                    wordBreak: "break-word",
                    textAlign: "left",
                  }}
                >
                  {profile.bio}
                </Typography>
              </Box>
            ) : null}
          </Box>

          {profile.is_owner ? (
            <Button
              variant="outlined"
              startIcon={<EditOutlinedIcon />}
              onClick={() => navigate("/profile")}
              sx={{
                display: { xs: "none", sm: "inline-flex" },
                color: "#111",
                borderColor: "#dfdfdf",
                textTransform: "none",
                fontWeight: 700,
                "&:hover": { borderColor: "#f7b900", bgcolor: "#fffaf0" },
              }}
            >
              Edit profile
            </Button>
          ) : null}
        </Box>

        <Divider sx={{ my: 2.5 }} />
        <Stack direction="row" spacing={5}>
          {profile.can_use_recommender ? (
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: 18 }}>{fieldCount}</Typography>
              <Typography sx={{ color: "text.secondary", fontSize: 13 }}>Fields</Typography>
            </Box>
          ) : null}
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: 18 }}>{publishedPosts.length}</Typography>
            <Typography sx={{ color: "text.secondary", fontSize: 13 }}>Published posts</Typography>
          </Box>
        </Stack>
      </Card>

      {profile.can_use_recommender ? (
        <FieldsPanel
          username={handle}
          ownerName={displayName}
          onCountChange={setFieldCount}
        />
      ) : null}

      <Card
        sx={{
          mt: 3,
          bgcolor: "#fff",
          border: "1px solid #e4e4e4",
          borderRadius: 2,
          boxShadow: "0 2px 8px rgba(15, 23, 42, 0.08)",
          overflow: "hidden",
        }}
      >
        <Box sx={{ px: 2.5, py: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            {postsTitle}
          </Typography>
          <Typography sx={{ color: "text.secondary", fontSize: 14 }}>
            {publishedPosts.length} published post(s)
          </Typography>
        </Box>
        <Divider />

        <Box sx={{ p: 2.5 }}>
          {publishedPosts.length === 0 ? (
            <Typography sx={{ color: "text.secondary" }}>No published posts yet.</Typography>
          ) : (
            <>
              <Stack spacing={1.5}>
                {visiblePublishedPosts.map((post) => (
                  <Box
                    key={post.slug || post.id}
                    onClick={() => navigate(`/blog/${post.id || post.slug}`)}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "110px 1fr auto" },
                      gap: { xs: 1.5, sm: 2 },
                      alignItems: { xs: "stretch", sm: "center" },
                      p: 1.5,
                      border: "1px solid #e5e5e5",
                      borderRadius: 1.5,
                      cursor: "pointer",
                      bgcolor: "#fff",
                      "&:hover": { borderColor: "#f7b900", bgcolor: "#fffdf4" },
                    }}
                  >
                    <Box
                      component="img"
                      src={post.image || "/images/bannerImage.jpg"}
                      alt={post.title}
                      sx={{
                        width: "100%",
                        height: { xs: 160, sm: 80 },
                        objectFit: "cover",
                        borderRadius: 1,
                      }}
                    />

                    <Box sx={{ minWidth: 0 }}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75, flexWrap: "wrap", rowGap: 0.75 }}>
                        {post.category ? <Chip label={post.category} size="small" sx={categoryChipSx(post.category)} /> : null}
                        {post.date ? (
                          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: "text.secondary", fontSize: 12 }}>
                            <CalendarTodayOutlinedIcon sx={{ fontSize: 14 }} />
                            <span>{formatDate(post.date)}</span>
                          </Stack>
                        ) : null}
                      </Stack>
                      <Typography sx={{ fontWeight: 800, overflowWrap: "anywhere" }}>
                        {post.title}
                      </Typography>
                      {post.excerpt ? (
                        <Typography sx={{ color: "text.secondary", fontSize: 13, mt: 0.25, overflowWrap: "anywhere" }}>
                          {post.excerpt}
                        </Typography>
                      ) : null}
                      {Array.isArray(post.tags) && post.tags.length > 0 ? (
                        <Stack direction="row" spacing={0.75} sx={{ mt: 1, flexWrap: "wrap", rowGap: 0.75 }}>
                          {post.tags.slice(0, 3).map((tag) => (
                            <Chip
                              key={tag}
                              icon={<LocalOfferOutlinedIcon />}
                              label={tag}
                              size="small"
                              sx={{
                                height: 22,
                                fontSize: 11,
                                bgcolor: "#f7f7f7",
                                color: "text.secondary",
                                "& .MuiChip-icon": { ml: 0.5, fontSize: 12 },
                              }}
                            />
                          ))}
                        </Stack>
                      ) : null}
                    </Box>

                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{
    color: "text.secondary",
    fontSize: 12,
    justifySelf: "end",
    alignSelf: "end",
    pb: 0.5,
  }}>
                      <ArticleOutlinedIcon sx={{ fontSize: 15 }} />
                      <span>{post.comments_count || 0} comments</span>
                    </Stack>
                  </Box>
                ))}
              </Stack>

              {publishedPosts.length > POSTS_PER_PAGE ? (
                <Stack alignItems="center" sx={{ mt: 3 }}>
                  <Pagination
                    count={publishedPageCount}
                    page={publishedPage}
                    onChange={(_, value) => setPublishedPage(value)}
                    showFirstButton
                    showLastButton
                    siblingCount={1}
                    boundaryCount={1}
                    sx={{
                      "& .MuiPagination-ul": { gap: 0.75 },
                      "& .MuiPaginationItem-root": {
                        border: "1px solid #efd36c",
                        color: "#9a7000",
                        bgcolor: "#fff",
                        minWidth: 38,
                        height: 38,
                        borderRadius: 1.25,
                        fontSize: 13,
                      },
                      "& .MuiPaginationItem-root.Mui-selected": {
                        color: "#312600",
                        bgcolor: "#f7b900",
                        backgroundImage: "none",
                        borderColor: "#f7b900",
                        "&:hover": { bgcolor: "#dca600" },
                      },
                    }}
                  />
                </Stack>
              ) : null}
            </>
          )}
        </Box>
      </Card>
    </Box>
  );
}
